/**
 * Local availability / stock system for the order model.
 *
 * The POS must not oversell while offline. The local source of truth is the
 * Dexie catalog — `timeslots.availability/booked` for dated stock, price
 * `quantity` (inside the productCategories documents) for retail stock.
 * Those numbers NEVER include what's sitting unpaid in the basket, so the
 * system is built from five cooperating parts, all in this module:
 *
 * 1. PENDING COUNTER — `countPendingOrderQuantity`: sums the selected
 *    order's current-session sale-line quantities that consume a given
 *    card's stock (shared-stock ∩ + time overlap → timeslot id → price
 *    uuid, in that precedence), weighted by stock-consumption units (a
 *    ticket can consume several units of a stock entity —
 *    `pc.stockConsumptions` / `getPcStockUnits`) and expressed in the
 *    displayed card's tickets. Everything availability-shaped subtracts it:
 *    the display (`isProductAvailable`) and the package's enforcement gates
 *    (`OrderDataProvider.checkAvailability`, called by the orders package
 *    with the FULL quantity on line creation and with the INCREASE DIFF on
 *    upserts — the subtraction is exact for both because a created line
 *    isn't in the session yet, while an increased line is pending at its
 *    old quantity).
 *
 * 2. STOCK INDEX — `categoryStockIndex` + `setupOrderStockIndexing`: the
 *    counter's only catalog dependency (line category uuid → stockIds),
 *    filled async from Dexie by a watcher on the selected order's lines so
 *    the counter itself stays synchronous for component computeds.
 *
 * 3. BACKEND PULL — `syncOrderAvailabilities`: refreshes the Dexie numbers
 *    for everything the pending lines consume (incl. shared-stock siblings).
 *    Triggered fire-and-forget after `addToBasket` and awaited when the
 *    payment screen opens.
 *
 * 4. COVERAGE CHECK — `checkOrderAvailabilityCoverage`: after every pull,
 *    flags basket groups whose pending quantity no longer fits the fresh
 *    numbers (another till sold the same stock) and opens the blocking
 *    AvailabilityInfo modal, which removes them (addon children included).
 *
 * 5. COMPLETION REFLECTION — `reflectCompletedSessionInLocalStock`: when a
 *    session completes, its NET delta (sale − cancel lines) is written into
 *    the Dexie numbers, once per session (persisted reflected-session-id
 *    guard), so sequential offline orders see each other's consumption.
 *    Cancellations/uncancels need no extra wiring: they only become
 *    definitive at completion of the session carrying the cancel lines, and
 *    the net delta covers them (a cancel-heavy session nets negative and
 *    RESTORES stock).
 *
 * Lifecycle: sessions partition an order's life, so "pending" is always
 * exactly the current non-completed session (completion removes lines from
 * the pending set by state change — no per-unit tracking needed). A full
 * catalog re-pull (config reload)
 * resets the baseline to server truth; closing sales wipes orders and calls
 * `clearReflectedSessions()`.
 *
 * Cross-file wiring map, lifecycle and operational limits:
 * docs/availability.md.
 */
import { ref, watch } from "vue";
import dayjs from "dayjs";
import { LineType, OrderLine } from "@smeetz/order-common";
import { OrderJson, OrderSessionState } from "@smeetz/orders";
import { useOrderStore } from "@smeetz/order-store";
import {
  getProductCategoriesByPriceUuids,
  getProductCategoriesByUuids,
} from "@/dexie/product-category";
import { getTimeslotsByIds } from "@/dexie/timeslots";
import { LogLevel, LogService } from "@/services/logging";
import { LocalStorageKeys } from "@/services/storage.service";
import AppModule from "@/store/modules/app";
import { ISyncUpdates } from "@/types/db/timeslots";
import {
  syncUpdatedTimeslots,
  TimeslotSyncEntry,
  updateTimeslots,
} from "@/utils/helpers/timeslots";
import {
  syncUpdatedRetails,
  RetailSyncEntry,
  sellOnSoldOut,
  updatePrices,
} from "@/utils/helpers/product-category";
import ModalModule from "@/store/modules/modal";
import { isPaymentInProgress } from "@/services/payment.service";
import { getPcStockUnits } from "@/utils/helpers/stock";
import { ModalStatus } from "@/constants/modal";

/**
 * Identity of a product card (or an availability request) on the catalog
 * side, used to match order lines that consume the same stock.
 */
export interface StockMatchTarget {
  /** Stock entities the card's category belongs to (`IProductCategory.stockIds`). */
  stockIds: number[];
  /**
   * Stock units ONE ticket of the card's category consumes
   * (`getPcStockUnits(pc.stockConsumptions)`). Defaults to 1.
   */
  stockUnits?: number;
  /** Uuids of the card's prices (`IProductSlotPrice.uuid`). */
  priceUuids: string[];
  /** The card's timeslot, when it represents dated stock. */
  timeSlot?: {
    id: number;
    startDateTime: string;
    endDateTime: string;
  };
}

/**
 * Reactive index `category uuid → { stockIds, stockUnits }`, the pending
 * counter's only catalog dependency: the stock entities a line's category
 * belongs to, and how many units of them ONE ticket consumes. Filled
 * asynchronously from the Dexie catalog by `setupOrderStockIndexing`'s
 * watcher; read synchronously by `countPendingOrderQuantity` so availability
 * stays computable inside component computeds. Until a category is indexed,
 * the shared-stock rule simply doesn't match it yet.
 */
const categoryStockIndex = ref<
  Record<string, { stockIds: number[]; stockUnits: number }>
>({});

/** Category uuids currently being fetched, so bursts don't re-query Dexie. */
const categoriesBeingIndexed = new Set<string>();

/**
 * Ensures the given categories are present in the stock index.
 */
async function indexCategories(categoryUuids: string[]): Promise<void> {
  const missing = categoryUuids.filter(
    (uuid) =>
      uuid &&
      !(uuid in categoryStockIndex.value) &&
      !categoriesBeingIndexed.has(uuid)
  );
  if (!missing.length) {
    return;
  }
  missing.forEach((uuid) => categoriesBeingIndexed.add(uuid));
  try {
    const categories = await getProductCategoriesByUuids(missing);
    const updated = { ...categoryStockIndex.value };
    for (const uuid of missing) {
      const category = categories.find((c) => c.uuid === uuid);
      updated[uuid] = {
        stockIds: category?.stockIds ?? [],
        stockUnits: getPcStockUnits(category?.stockConsumptions),
      };
    }
    categoryStockIndex.value = updated;
  } catch (error) {
    LogService.info(
      "Error indexing category stock ids",
      {
        categoryUuids: missing,
        error,
      },
      LogLevel.Error
    );
  } finally {
    missing.forEach((uuid) => categoriesBeingIndexed.delete(uuid));
  }
}

/**
 * Installs the watcher that keeps the stock index warm: whenever the selected
 * order's current-session lines change, their categories are indexed. Call
 * once during app bootstrap.
 */
export function setupOrderStockIndexing(): void {
  const orderStore = useOrderStore();
  watch(
    () =>
      orderStore.selectedOrder?.orderSession?.orderLines?.map(
        (line) => line.categoryId
      ) ?? [],
    (categoryUuids) => {
      if (categoryUuids.length) {
        void indexCategories([...new Set(categoryUuids)]);
      }
    },
    { immediate: true }
  );
}

/**
 * Pending sale lines of the selected order: current session only (previous
 * sessions are completed, so their units already live inside the catalog
 * availability numbers), and only while the session itself isn't completed —
 * completion moves the units into the local stock numbers (W3), so a
 * completed session must not count as pending. Cancel lines are ignored:
 * they target already-completed stock, which the cancellation flow (W3)
 * reflects directly in the catalog numbers.
 */
function pendingSaleLines(): OrderLine[] {
  const session = useOrderStore().selectedOrder?.orderSession;
  if (!session || session.state === OrderSessionState.COMPLETED) {
    return [];
  }
  return (session.orderLines ?? []).filter(
    (line) => line.lineType === LineType.SaleLine
  );
}

/**
 * Pulls fresh availability from the backend for everything the selected
 * order's pending lines consume — their categories, prices, shared-stock
 * siblings and time ranges — and writes it into the local Dexie catalog.
 * Fire-and-forget from the basket flow (errors are logged, never thrown);
 * awaited on the payment screen as a last pre-payment refresh.
 */
export async function syncOrderAvailabilities(): Promise<void> {
  const lines = pendingSaleLines();
  if (!lines.length) {
    return;
  }
  const reference = useOrderStore().selectedOrder?.reference;

  const categoryUuids = [...new Set(lines.map((line) => line.categoryId))];
  const categories = await getProductCategoriesByUuids(categoryUuids);
  const categoriesByUuid = new Map(categories.map((pc) => [pc.uuid, pc]));

  const timeslotEntries: TimeslotSyncEntry[] = [];
  const retailEntries: RetailSyncEntry[] = [];
  for (const line of lines) {
    const pc = categoriesByUuid.get(line.categoryId);
    if (!pc) {
      continue;
    }
    if (line.startDateTime && line.endDateTime) {
      timeslotEntries.push({
        categoryId: pc.id,
        stockIds: pc.stockIds ?? [],
        startDateTime: String(line.startDateTime),
        endDateTime: String(line.endDateTime),
      });
    }
    if (!line.timeSlotId) {
      const numericPriceId = pc.prices?.find(
        (price) => price.uuid === line.priceId
      )?.id;
      if (numericPriceId) {
        retailEntries.push({
          priceId: numericPriceId,
          stockIds: pc.stockIds ?? [],
        });
      }
    }
  }

  try {
    AppModule.setIsBackgroundLoading(true);
    await syncUpdatedTimeslots(timeslotEntries);
  } catch (error) {
    LogService.info(
      `Availability: Error when synching timeslots for order ${reference}`,
      { error },
      LogLevel.Error
    );
  } finally {
    AppModule.setIsBackgroundLoading(false);
  }

  try {
    AppModule.setIsBackgroundLoading(true);
    await syncUpdatedRetails(retailEntries);
  } catch (error) {
    LogService.info(
      `Availability: Error when synching retail prices for order ${reference}`,
      { error },
      LogLevel.Error
    );
  } finally {
    AppModule.setIsBackgroundLoading(false);
  }

  // The pull may have shrunk availability below what the basket already
  // holds (another till sold the same stock). Surface that to the cashier.
  try {
    await checkOrderAvailabilityCoverage();
  } catch (error) {
    LogService.info(
      `Availability: Error checking basket coverage for order ${reference}`,
      { error },
      LogLevel.Error
    );
  }
}

/**
 * One basket group whose pending quantity exceeds the fresh availability,
 * shown by the AvailabilityInfo modal and removed on acknowledgement.
 */
export interface UncoveredAvailability {
  categoryName: string;
  priceName: string;
  dbAvailability: number;
  bookedQty: number;
  /** The group's lines plus their addon children — removed on confirm. */
  orderLineIds: string[];
}

/**
 * Uncovered basket groups awaiting cashier acknowledgement. Written by
 * `checkOrderAvailabilityCoverage`, read by the AvailabilityInfo modal,
 * cleared when the modal removes the lines.
 */
export const uncoveredAvailability = ref<UncoveredAvailability[]>([]);

/**
 * Compares every pending sale line against the fresh catalog numbers (run
 * this AFTER a pull) and flags the groups whose pending quantity — including
 * shared-stock neighbours — no longer fits. Skipped: FlipLab overbooking
 * stock (`-1000` sentinel) and retail prices flagged sell-on-sold-out.
 * When anything is uncovered, opens the AvailabilityInfo modal.
 */
async function checkOrderAvailabilityCoverage(): Promise<void> {
  if (isPaymentInProgress.value) {
    // Never trim the basket while a terminal payment is processing — the
    // order being charged must not change under the payment.
    return;
  }
  const lines = pendingSaleLines();
  if (!lines.length) {
    uncoveredAvailability.value = [];
    return;
  }

  // Fresh reads — the pull has just rewritten these Dexie rows.
  const categoryUuids = [...new Set(lines.map((line) => line.categoryId))];
  const categories = await getProductCategoriesByUuids(categoryUuids);
  const categoriesByUuid = new Map(categories.map((pc) => [pc.uuid, pc]));
  const timeslotIds = [
    ...new Set(
      lines
        .filter((line) => line.timeSlotId)
        .map((line) => line.timeSlotId as number)
    ),
  ];
  const timeslots = timeslotIds.length
    ? await getTimeslotsByIds(timeslotIds)
    : [];

  const uncovered = new Map<string, UncoveredAvailability>();
  for (const line of lines) {
    const pc = categoriesByUuid.get(line.categoryId);
    if (!pc) {
      continue;
    }
    if (pc.stockIds?.includes(-1000)) {
      continue;
    }

    let dbAvailability: number;
    if (line.timeSlotId) {
      dbAvailability =
        timeslots.find((ts) => ts.id === line.timeSlotId)?.availability ?? 0;
    } else {
      if (sellOnSoldOut(pc.prices, pc.type)) {
        continue;
      }
      dbAvailability =
        pc.prices?.find((price) => price.uuid === line.priceId)?.quantity ?? 0;
    }

    const bookedQty = countPendingOrderQuantity({
      stockIds: pc.stockIds ?? [],
      stockUnits: getPcStockUnits(pc.stockConsumptions),
      priceUuids: [line.priceId],
      timeSlot:
        line.timeSlotId && line.startDateTime && line.endDateTime
          ? {
              id: line.timeSlotId,
              startDateTime: String(line.startDateTime),
              endDateTime: String(line.endDateTime),
            }
          : undefined,
    });
    if (bookedQty <= dbAvailability) {
      continue;
    }

    const key = `${line.priceId}|${line.timeSlotId ?? ""}`;
    const group = uncovered.get(key) ?? {
      categoryName: line.categoryName,
      priceName: line.priceName,
      dbAvailability,
      bookedQty,
      orderLineIds: [],
    };
    group.orderLineIds.push(line.id);
    uncovered.set(key, group);
  }

  if (!uncovered.size) {
    uncoveredAvailability.value = [];
    return;
  }

  // Addon children ride along with their uncovered main line.
  const groups = [...uncovered.values()];
  const uncoveredIds = new Set(groups.flatMap((group) => group.orderLineIds));
  const session = useOrderStore().selectedOrder?.orderSession;
  for (const line of session?.orderLines ?? []) {
    if (line.mainLineId && uncoveredIds.has(line.mainLineId)) {
      const group = groups.find((g) =>
        g.orderLineIds.includes(line.mainLineId as string)
      );
      group?.orderLineIds.push(line.id);
    }
  }

  uncoveredAvailability.value = groups;
  LogService.info(
    "Availability: basket exceeds fresh availability, informing the cashier",
    { params: { uncovered: groups } }
  );
  ModalModule.setStatus(ModalStatus.AvailabilityInfo);
}

/**
 * Sums the quantities of the selected order's pending sale lines that consume
 * the same stock as the given card. Matching precedence per line:
 *
 * 1. The line's category shares a stock entity with the card's category —
 *    counted when the card has no timeslot, or when the line's time range
 *    overlaps the card's timeslot.
 * 2. Otherwise, when the card has a timeslot: counted on the same timeslot id.
 * 3. Otherwise (no timeslot on the card): counted on the same price uuid.
 *
 * The result is what the availability display (and the availability gate)
 * subtracts from the catalog numbers, which never include pending lines.
 */
export function countPendingOrderQuantity(target: StockMatchTarget): number {
  // Counting happens in STOCK UNITS (a ticket can consume several units of a
  // stock entity), then converts back into tickets of the displayed card.
  const ownUnits = Math.max(1, target.stockUnits ?? 1);
  let count = 0;
  for (const line of pendingSaleLines()) {
    const indexEntry = categoryStockIndex.value[line.categoryId];
    const lineStockIds = indexEntry?.stockIds ?? [];
    const lineUnits = Math.max(1, indexEntry?.stockUnits ?? 1);
    const sharesStock =
      target.stockIds.length > 0 &&
      lineStockIds.some((stockId) => target.stockIds.includes(stockId));

    if (sharesStock) {
      // A foreign line drains the shared stock by ITS OWN units per ticket.
      if (target.timeSlot) {
        if (
          line.startDateTime &&
          line.endDateTime &&
          dayjs(line.startDateTime).isBefore(target.timeSlot.endDateTime) &&
          dayjs(line.endDateTime).isAfter(target.timeSlot.startDateTime)
        ) {
          count += line.quantity * lineUnits;
        }
      } else {
        count += line.quantity * lineUnits;
      }
    } else if (target.timeSlot) {
      if (line.timeSlotId === target.timeSlot.id) {
        count += line.quantity * ownUnits;
      }
    } else if (target.priceUuids.includes(line.priceId)) {
      count += line.quantity * ownUnits;
    }
  }
  // dbAvailability is denominated in the displayed card's tickets, so a
  // partial drain of another card's units still blocks a whole ticket here.
  return Math.ceil(count / ownUnits);
}

// --- Completed-session stock reflection ---------------------------------
// When a session completes, its units become part of the sold reality but
// the local catalog numbers don't know it until the next backend pull. The
// functions below apply the completed session's NET stock delta (sale lines
// minus cancel lines) to the Dexie catalog immediately, keeping offline
// oversell protection intact across sequential orders. Sessions partition an
// order's life, so each completion carries exactly the not-yet-applied delta
// (an edit session holds only its addition/cancel lines) — the persisted
// reflected-session-id set below is a crash/retry guard on top, not the core
// mechanism.

/** Reflected session ids beyond this count are pruned oldest-first. */
const MAX_REFLECTED_SESSIONS = 200;

let reflectedSessionIds: Set<string> | null = null;

function loadReflectedSessionIds(): Set<string> {
  if (!reflectedSessionIds) {
    try {
      const stored = JSON.parse(
        localStorage.getItem(LocalStorageKeys.ReflectedStockSessions) || "[]"
      );
      reflectedSessionIds = new Set(Array.isArray(stored) ? stored : []);
    } catch {
      reflectedSessionIds = new Set();
    }
  }
  return reflectedSessionIds;
}

function markSessionReflected(sessionId: string): void {
  const ids = loadReflectedSessionIds();
  ids.add(sessionId);
  while (ids.size > MAX_REFLECTED_SESSIONS) {
    ids.delete(ids.values().next().value as string);
  }
  localStorage.setItem(
    LocalStorageKeys.ReflectedStockSessions,
    JSON.stringify([...ids])
  );
}

/**
 * Forgets all reflected sessions. Call when closing sales: the orders are
 * wiped there, so no completed session can be re-applied afterwards.
 */
export function clearReflectedSessions(): void {
  reflectedSessionIds = new Set();
  localStorage.removeItem(LocalStorageKeys.ReflectedStockSessions);
}

/**
 * Net stock effect of a session's lines: sale lines consume, cancel lines
 * restore. Dated stock is keyed by numeric timeslot id, retail stock by
 * price uuid (the Dexie catalog carries price uuids; timeslots have none).
 */
function computeSessionStockDelta(lines: OrderLine[]): {
  timeslotDelta: Map<number, number>;
  retailDelta: Map<string, number>;
} {
  const timeslotDelta = new Map<number, number>();
  const retailDelta = new Map<string, number>();
  for (const line of lines) {
    let sign: number;
    if (line.lineType === LineType.SaleLine) {
      sign = 1;
    } else if (line.lineType === LineType.CancelLine) {
      sign = -1;
    } else {
      continue;
    }
    const quantity = sign * Math.abs(line.quantity);
    if (line.timeSlotId) {
      timeslotDelta.set(
        line.timeSlotId,
        (timeslotDelta.get(line.timeSlotId) ?? 0) + quantity
      );
    } else {
      retailDelta.set(
        line.priceId,
        (retailDelta.get(line.priceId) ?? 0) + quantity
      );
    }
  }
  return { timeslotDelta, retailDelta };
}

/**
 * Applies the completed session of the given order to the local catalog
 * numbers, exactly once per session (idempotent across retries and reloads).
 * Never throws — the sale is already final when this runs, so failures are
 * logged and the next backend pull corrects the numbers.
 */
export async function reflectCompletedSessionInLocalStock(
  order: OrderJson | null | undefined
): Promise<void> {
  try {
    const session = order?.orderSession;
    if (!session?.id || session.state !== OrderSessionState.COMPLETED) {
      return;
    }
    if (loadReflectedSessionIds().has(session.id)) {
      return;
    }

    const { timeslotDelta, retailDelta } = computeSessionStockDelta(
      session.orderLines ?? []
    );
    LogService.info(
      `Availability: reflecting completed session ${session.id} of order ${order?.reference} in local stock`,
      {
        params: {
          timeslotDelta: [...timeslotDelta.entries()],
          retailDelta: [...retailDelta.entries()],
        },
      }
    );

    AppModule.updatedIsLoading(true);
    try {
      if (timeslotDelta.size) {
        await applyTimeslotDelta(timeslotDelta);
      }
      if (retailDelta.size) {
        await applyRetailDelta(retailDelta);
      }
    } finally {
      AppModule.updatedIsLoading(false);
    }

    markSessionReflected(session.id);
  } catch (error) {
    LogService.info(
      `Availability: Error reflecting completed session in local stock for order ${order?.reference}`,
      { error },
      LogLevel.Error
    );
  }
}

/**
 * Writes a net dated-stock delta into the Dexie timeslots: `availability`
 * and `estimatedTotalCapacity` shrink by the consumed quantity, `booked`
 * grows by it (negative deltas restore). `bookable` and `availabilityUpdated`
 * are written back unchanged so the backend pull stays authoritative for them.
 */
async function applyTimeslotDelta(delta: Map<number, number>): Promise<void> {
  const dbTimeslots = await getTimeslotsByIds([...delta.keys()]);
  const updates: ISyncUpdates[] = [];
  for (const ts of dbTimeslots) {
    const quantity = delta.get(ts.id) ?? 0;
    if (!quantity) {
      continue;
    }
    updates.push({
      id: String(ts.id),
      fields: [
        { key: "availability", value: String(ts.availability - quantity) },
        { key: "booked", value: String(ts.booked + quantity) },
        {
          key: "estimatedTotalCapacity",
          value: String(ts.estimatedTotalCapacity - quantity),
        },
        { key: "bookable", value: String(ts.bookable) },
        { key: "availabilityUpdated", value: String(ts.availabilityUpdated) },
      ],
    });
  }
  if (updates.length) {
    await updateTimeslots(updates);
  }
}

/**
 * Writes a net retail-stock delta into the Dexie catalog prices, matched by
 * price uuid inside the pc documents. `updatedAt` is written back unchanged
 * so the backend pull stays authoritative for it.
 */
async function applyRetailDelta(delta: Map<string, number>): Promise<void> {
  const categories = await getProductCategoriesByPriceUuids([...delta.keys()]);
  const updates: ISyncUpdates[] = [];
  for (const [priceUuid, quantity] of delta) {
    if (!quantity) {
      continue;
    }
    const pc = categories.find((c) =>
      c.prices?.some((price) => price.uuid === priceUuid)
    );
    const price = pc?.prices.find((p) => p.uuid === priceUuid);
    if (!price) {
      continue;
    }
    updates.push({
      id: String(price.id),
      fields: [
        { key: "quantity", value: String(price.quantity - quantity) },
        { key: "updatedAt", value: price.updatedAt?.toString() || "" },
      ],
    });
  }
  if (updates.length) {
    await updatePrices(updates);
  }
}
